/**
 * Editor (spec 017 US3) — the ADMIN / super-admin control surface (role-gated; NOT a
 * customer-facing thing, and distinct from the customer "Studio" plan/edition). It hosts
 * template authoring/management, the unified Option Groups authoring, brand approvals,
 * and settings as sub-tabs. (Formerly these lived loose in the customer nav.)
 */
import { useEffect, useState } from "react";
import { InfoHint } from "../ui/Tooltip.js";
import { Tabs } from "../ui/Tabs.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { TemplatesPanel } from "../templates/TemplatesPanel.js";
import { OptionsPanel } from "../options/OptionsPanel.js";
import { BrandsAdmin } from "../branding/BrandsAdmin.js";
import { CatalogAdmin } from "../catalog/CatalogAdmin.js";
import { SettingsPanel } from "../settings/SettingsPanel.js";
import { TransparentPanel } from "../transparent/TransparentPanel.js";
import { useWizardStore } from "../stores/wizard.js";

type EditorTab = "templates" | "options" | "brands" | "catalog" | "transparent" | "settings";

const TABS: { key: EditorTab; label: string; hint: string }[] = [
  { key: "templates", label: "Templates", hint: "Author & approve" },
  { key: "options", label: "Options", hint: "Dropdowns & harness" },
  { key: "brands", label: "Brands", hint: "Manage & approve" },
  { key: "catalog", label: "Catalog", hint: "Models & styles" },
  { key: "transparent", label: "Transparent", hint: "Print guide \u2192 transparent overlays" },
  { key: "settings", label: "Settings", hint: "Workspace" },
];

export function Editor() {
  const [tab, setTab] = useState<EditorTab>("templates");
  // A wizard opened from another page ("Make template" on Only mock-ups) shows on Templates.
  const wizardOpen = useWizardStore((s) => s.open && (s.origin ?? "editor") === "editor");
  useEffect(() => { if (wizardOpen) setTab("templates"); }, [wizardOpen]);

  return (
    <section className="workspace" data-testid="editor">
      <header className="panel-head">
        <div>
          <h1 className="panel-title">
            Editor
            <InfoHint text="Author templates, option groups, and brands — the controls that power every customer studio." />
          </h1>
        </div>
      </header>
      {/* Keyed by tab: a crash in one tab leaves the others (and the tab strip itself)
          working, and switching away and back remounts the boundary clean. */}
      <Tabs
        tabs={TABS}
        active={tab}
        onChange={(k) => setTab(k as EditorTab)}
        label="Editor"
        idPrefix="editor"
      >
        <ErrorBoundary key={tab} name={TABS.find((t) => t.key === tab)?.label ?? "Editor"}>
          {tab === "templates" && <TemplatesPanel pageTitle={false} />}
          {tab === "options" && <OptionsPanel />}
          {tab === "brands" && <BrandsAdmin />}
          {tab === "catalog" && <CatalogAdmin />}
          {tab === "transparent" && <TransparentPanel />}
          {tab === "settings" && <SettingsPanel pageTitle={false} />}
        </ErrorBoundary>
      </Tabs>
    </section>
  );
}
